import { cn } from "./classNames-DkX30VJU.js";
import { Tooltip, TooltipProvider } from "./utils-DGd7lPd6.js";
import { mergeRefs } from "./internal/utils/mergeRefs.js";
import { ChevronDown, Search } from "./icons-DDR9dFSr.js";
import { DropdownMenu } from "./DropdownMenu-Ba9atqDa.js";
import { ScrollArea } from "./internal/components/ScrollArea.js";
import { Sheet } from "./Sheet-C6PbG8NI.js";
import { HoverCard } from "./HoverCard-OGWKRXJ7.js";
import { Separator } from "./internal/components/Separator.js";
import { useElement } from "./useElement-Dcoo9Jlr.js";
import { Collapsible } from "./Collapsible-zsXk4Vpp.js";
import { useThemeScopeContainer } from "./ThemeProvider-DnSrwCFY.js";
import { BrandLogo } from "./BrandLogo-v7fffuv3.js";
import { useScrollbarPosition } from "./internal/hooks/useScrollbarPosition.js";
import { createContext, use, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from "react";
import { Root, Slot, Slottable } from "@radix-ui/react-slot";
import { jsx, jsxs } from "react/jsx-runtime";
// src/components/Sidebar/SidebarProvider/useTransitionPresence.ts
const FALLBACK_TIMEOUT_MS = 500;
/** Under reduced motion there is no `transitionend` to wait for, so the mount is not deferred. */
const prefersReducedMotion = () => typeof window !== "undefined" && (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
const reducer = (state, event) => {
	switch (event) {
		case "MOUNT": return "mounted";
		case "ENTER": return state === "unmounted" ? "enterSuspended" : "mounted";
		case "TRANSITION_OUT":
			if (state === "enterSuspended") return "unmounted";
			return state === "mounted" ? "unmountSuspended" : state;
		case "TRANSITION_END":
			if (state === "unmountSuspended") return "unmounted";
			if (state === "enterSuspended") return "mounted";
			return state;
		default: return state;
	}
};
const useTransitionPresence = ({ mounted, keepMounted = false, mountAfterEnter = false }) => {
	const [state, send] = useReducer(reducer, mounted ? "mounted" : "unmounted");
	useLayoutEffect(() => {
		if (keepMounted) return;
		if (!mounted) {
			send("TRANSITION_OUT");
			return;
		}
		send(mountAfterEnter && !prefersReducedMotion() ? "ENTER" : "MOUNT");
	}, [
		mounted,
		keepMounted,
		mountAfterEnter
	]);
	useEffect(() => {
		if (state !== "unmountSuspended" && state !== "enterSuspended") return;
		const id = setTimeout(() => send("TRANSITION_END"), FALLBACK_TIMEOUT_MS);
		return () => clearTimeout(id);
	}, [state]);
	return useMemo(() => ({
		isMounted: keepMounted || state === "mounted" || state === "unmountSuspended",
		/** Resolves whichever transition the subtree is waiting on. */
		completeUnmount: () => send("TRANSITION_END")
	}), [state, keepMounted]);
};
// src/components/Sidebar/SidebarContent.tsx
const SidebarContent = ({ ref, className, children, contentRef }) => {
	const { setElement, isBottom, isTop, isMiddle } = useScrollbarPosition();
	const mergedRefs = mergeRefs(ref ?? null, (ref) => setElement(ref));
	return /* @__PURE__ */ jsx(ScrollArea, {
		ref: mergedRefs,
		type: "scroll",
		className: "tw:group/sidebar-content tw:flex-1",
		"data-slot": "sidebar-content",
		scrollBarClassName: "tw:w-1.5",
		viewPortClassName: cn("tw:overflow-x-hidden", "tw:overscroll-contain", "tw:[&>div]:!flex", "tw:group-first/sidebar-content:before:hidden", "tw:group-last/sidebar-content:after:hidden", "tw:!box-border", {
			"tw:before:absolute tw:before:top-0 tw:before:left-0 tw:before:right-0 tw:before:h-px tw:before:bg-sidebar-border tw:before:z-10 tw:before:content-[\"\"]": isBottom || isMiddle,
			"tw:after:absolute tw:after:bottom-0 tw:after:left-0 tw:after:right-0 tw:after:h-px tw:after:bg-sidebar-border tw:after:z-10 tw:after:content-[\"\"]": isTop || isMiddle
		}),
		children: /* @__PURE__ */ jsx("div", {
			ref: contentRef,
			className: cn("tw:flex", "tw:flex-1", "tw:flex-col", "tw:gap-1", "tw:overflow-x-hidden", "tw:p-2", className),
			children
		})
	});
};
// src/hooks/useMediaQuery.ts
/**
* Whether a CSS media query currently matches, re-rendering when it flips.
*
* - `false` wherever `matchMedia` is absent (SSR, a stubbed test environment), so the
*   caller renders its desktop branch rather than crashing.
* - The subscription is the query's own `change` event — no resize listener, no polling.
*/
const useMediaQuery = (query) => {
	const subscribe = useCallback((onStoreChange) => {
		const list = globalThis.matchMedia?.(query);
		list?.addEventListener?.("change", onStoreChange);
		return () => list?.removeEventListener?.("change", onStoreChange);
	}, [query]);
	const getSnapshot = useCallback(() => globalThis.matchMedia?.(query).matches ?? false, [query]);
	return useSyncExternalStore(subscribe, getSnapshot, () => false);
};
// src/components/Sidebar/utils.ts
const SidebarWidthInPx = {
	MIN_DEFAULT: 256,
	COLLAPSED_ICON: 52,
	FLOATING_VARIANT_PADDING: 8
};
/**
* The query a rail watches to know whether it is a sheet.
*
* - `max-width`, so it is exclusive of the breakpoint itself — the width passed in is the
*   first one at which the rail is a column again.
* - Per side, because the width at which a rail stops fitting is the rail's own: a 320px
*   panel beside a capped reading column runs out of room long before a navigation rail does.
*/
const sidebarMobileMediaQuery = (breakpointPx) => `(max-width: ${breakpointPx - .02}px)`;
const floatingSideCount = {
	"sided-floating": 1,
	floating: 2,
	inset: 0,
	none: 0
};
const calcEffectiveWidths = ({ variant, width: customWidth, collapsible, state, mode, isMobile = false }) => {
	if (variant === "none" || isMobile) return {
		sidebar: 0,
		gap: 0,
		fill: 0
	};
	const padding = floatingSideCount[variant] * SidebarWidthInPx.FLOATING_VARIANT_PADDING;
	const width = Math.max(customWidth ?? 0, SidebarWidthInPx.MIN_DEFAULT) + padding;
	const iconWidth = SidebarWidthInPx.COLLAPSED_ICON + padding;
	const isExpanded = state === "expanded";
	const reduction = variant === "inset" ? 0 : SidebarWidthInPx.FLOATING_VARIANT_PADDING;
	const iconWidths = (() => {
		const sidebar = isExpanded ? width : iconWidth;
		const gap = mode === "expand-on-hover" ? iconWidth : sidebar;
		return {
			sidebar,
			gap,
			fill: gap - reduction
		};
	})();
	const offCanvasWidths = (() => {
		const sidebar = width;
		const gap = isExpanded ? width : SidebarWidthInPx.FLOATING_VARIANT_PADDING;
		return {
			sidebar,
			gap,
			fill: gap - reduction
		};
	})();
	return collapsible === "icon" ? iconWidths : offCanvasWidths;
};
// src/components/Sidebar/SidebarProvider/context.ts
const SidebarContext = createContext(null);
/** `useSidebar` that returns `null` outside a `SidebarProvider` instead of throwing. */
const useOptionalSidebar = (side = "left") => use(SidebarContext)?.[side] ?? null;
const useSidebar = (side = "left") => {
	const context = use(SidebarContext);
	if (context === null) throw new Error("useSidebar must be used within a SidebarProvider.");
	return context[side];
};
// src/components/Sidebar/SidebarProvider/useHoverTracking.ts
const PORTAL_OPEN_SELECTORS_STRING = ["[aria-haspopup=\"menu\"][aria-expanded=true]", "[aria-haspopup=\"dialog\"][aria-expanded=true]"].join(",");
const useHoverTracking = (sidebarElement) => {
	const [hoveredElement, setHoveredElement] = useState(null);
	const isLockedRef = useRef(false);
	if (hoveredElement !== null && hoveredElement !== sidebarElement) setHoveredElement(null);
	const isHovered = hoveredElement !== null;
	useEffect(() => {
		if (!sidebarElement) return;
		const handleMouseEnter = () => setHoveredElement(sidebarElement);
		const handleMouseLeave = () => {
			if (isLockedRef.current) return;
			setHoveredElement(null);
		};
		sidebarElement.addEventListener("mouseenter", handleMouseEnter);
		sidebarElement.addEventListener("mouseleave", handleMouseLeave);
		return () => {
			sidebarElement.removeEventListener("mouseenter", handleMouseEnter);
			sidebarElement.removeEventListener("mouseleave", handleMouseLeave);
		};
	}, [sidebarElement]);
	useEffect(() => {
		if (!sidebarElement) return;
		let animationFrameId;
		const observer = new MutationObserver(() => {
			const hasOpenPortal = sidebarElement.querySelector(PORTAL_OPEN_SELECTORS_STRING) !== null;
			isLockedRef.current = hasOpenPortal;
			if (hasOpenPortal) return;
			animationFrameId = requestAnimationFrame(() => {
				setHoveredElement(sidebarElement.matches(":hover") ? sidebarElement : null);
			});
		});
		isLockedRef.current = sidebarElement.querySelector(PORTAL_OPEN_SELECTORS_STRING) !== null;
		observer.observe(sidebarElement, {
			attributes: true,
			attributeFilter: ["aria-expanded"],
			subtree: true
		});
		return () => {
			cancelAnimationFrame(animationFrameId);
			observer.disconnect();
		};
	}, [sidebarElement]);
	return isHovered;
};
// src/components/Sidebar/SidebarProvider/useSidebarState.ts
const useSidebarState = ({ defaultMode, mode: modeProp, onModeChange }) => {
	const [sidebarElement, setSidebarElement] = useElement();
	const isHovered = useHoverTracking(sidebarElement);
	const [_mode, _setMode] = useState(defaultMode ?? "expanded");
	const mode = modeProp ?? _mode;
	const setMode = useCallback((dispatch) => {
		const newState = typeof dispatch === "function" ? dispatch(mode) : dispatch;
		(onModeChange ?? _setMode)(newState);
	}, [onModeChange, mode]);
	const toggle = useCallback(() => {
		setMode((currState) => currState === "expanded" ? "collapsed" : "expanded");
	}, [setMode]);
	const state = (() => {
		if (mode === "expand-on-hover") return isHovered ? "expanded" : "collapsed";
		return mode;
	})();
	return useMemo(() => ({
		isHovered,
		toggle,
		setMode,
		mode,
		state,
		sidebarElement,
		setSidebarElement
	}), [
		isHovered,
		toggle,
		setMode,
		mode,
		state,
		sidebarElement,
		setSidebarElement
	]);
};
// src/components/Sidebar/SidebarProvider/SidebarProvider.tsx
const DEFAULT_SIDEBAR_VARIANT = "none";
const DEFAULT_SIDEBAR_MODE = "expanded";
const DEFAULT_SIDEBAR_COLLAPSIBLE = "off-canvas";
/** A sheet's `setMode`: no-op, so `Sidebar.SearchInput` focus keeps a desktop `collapsed`. */
const ignoreModeChange = () => {};
/** The side's own breakpoint, or the default for `true`. */
const mobileBreakpointOf = (mobileSheet) => typeof mobileSheet === "number" ? mobileSheet : 768;
const SidebarProvider = ({ children, defaultLeftMode = DEFAULT_SIDEBAR_MODE, leftMode, onLeftModeChange, defaultRightMode = DEFAULT_SIDEBAR_MODE, rightMode, onRightModeChange, leftMobileSheet = false, rightMobileSheet = false, leftCollapsible = DEFAULT_SIDEBAR_COLLAPSIBLE, leftVariant = DEFAULT_SIDEBAR_VARIANT, leftWidth, rightCollapsible = DEFAULT_SIDEBAR_COLLAPSIBLE, rightVariant = DEFAULT_SIDEBAR_VARIANT, rightWidth, onLeftWidthChange, onRightWidthChange, defaultFilledSide = null }) => {
	const isLeftNarrow = useMediaQuery(sidebarMobileMediaQuery(mobileBreakpointOf(leftMobileSheet)));
	const isRightNarrow = useMediaQuery(sidebarMobileMediaQuery(mobileBreakpointOf(rightMobileSheet)));
	const isLeftMobile = isLeftNarrow && leftMobileSheet !== false && leftVariant !== "none";
	const isRightMobile = isRightNarrow && rightMobileSheet !== false && rightVariant !== "none";
	const [filledSide, setFilledSide] = useState(defaultFilledSide);
	const [openMobileSide, setOpenMobileSide] = useState(null);
	const [isLeftDragging, setIsLeftDragging] = useState(false);
	const [isRightDragging, setIsRightDragging] = useState(false);
	const [leftDragWidth, setLeftDragWidth] = useState(null);
	const [rightDragWidth, setRightDragWidth] = useState(null);
	if (leftDragWidth !== null && leftWidth === leftDragWidth) setLeftDragWidth(null);
	if (rightDragWidth !== null && rightWidth === rightDragWidth) setRightDragWidth(null);
	if (openMobileSide === "left" && !isLeftMobile) setOpenMobileSide(null);
	if (openMobileSide === "right" && !isRightMobile) setOpenMobileSide(null);
	const leftState = useSidebarState({
		defaultMode: defaultLeftMode,
		mode: leftMode,
		onModeChange: onLeftModeChange
	});
	const rightState = useSidebarState({
		defaultMode: defaultRightMode,
		mode: rightMode,
		onModeChange: onRightModeChange
	});
	const toggleLeftMobile = useCallback(() => setOpenMobileSide((prev) => prev === "left" ? null : "left"), []);
	const toggleRightMobile = useCallback(() => setOpenMobileSide((prev) => prev === "right" ? null : "right"), []);
	const setLeftMobileOpen = useCallback((open) => setOpenMobileSide((prev) => open ? "left" : prev === "left" ? null : prev), []);
	const setRightMobileOpen = useCallback((open) => setOpenMobileSide((prev) => open ? "right" : prev === "right" ? null : prev), []);
	const toggleLeftFill = useCallback(() => setFilledSide((prev) => prev === "left" ? null : "left"), []);
	const toggleRightFill = useCallback(() => setFilledSide((prev) => prev === "right" ? null : "right"), []);
	const setLeftFill = useCallback((fill) => setFilledSide((prev) => fill ? "left" : prev === "left" ? null : prev), []);
	const setRightFill = useCallback((fill) => setFilledSide((prev) => fill ? "right" : prev === "right" ? null : prev), []);
	const value = useMemo(() => ({
		left: {
			...leftState,
			...isLeftMobile ? {
				mode: "expanded",
				state: "expanded",
				toggle: toggleLeftMobile,
				setMode: ignoreModeChange
			} : null,
			isMobile: isLeftMobile,
			isMobileOpen: openMobileSide === "left",
			setMobileOpen: setLeftMobileOpen,
			isFill: filledSide === "left",
			toggleFill: toggleLeftFill,
			setFill: setLeftFill,
			widths: calcEffectiveWidths({
				variant: leftVariant,
				width: leftDragWidth ?? leftWidth,
				collapsible: leftCollapsible,
				state: leftState.state,
				mode: leftState.mode,
				isMobile: isLeftMobile
			}),
			collapsible: leftCollapsible,
			variant: leftVariant,
			baseWidth: leftDragWidth ?? leftWidth,
			onWidthChange: onLeftWidthChange,
			setDragWidth: setLeftDragWidth,
			isDragging: isLeftDragging,
			setIsDragging: setIsLeftDragging
		},
		right: {
			...rightState,
			...isRightMobile ? {
				mode: "expanded",
				state: "expanded",
				toggle: toggleRightMobile,
				setMode: ignoreModeChange
			} : null,
			isMobile: isRightMobile,
			isMobileOpen: openMobileSide === "right",
			setMobileOpen: setRightMobileOpen,
			isFill: filledSide === "right",
			toggleFill: toggleRightFill,
			setFill: setRightFill,
			widths: calcEffectiveWidths({
				variant: rightVariant,
				width: rightDragWidth ?? rightWidth,
				collapsible: rightCollapsible,
				state: rightState.state,
				mode: rightState.mode,
				isMobile: isRightMobile
			}),
			collapsible: rightCollapsible,
			variant: rightVariant,
			baseWidth: rightDragWidth ?? rightWidth,
			onWidthChange: onRightWidthChange,
			setDragWidth: setRightDragWidth,
			isDragging: isRightDragging,
			setIsDragging: setIsRightDragging
		}
	}), [
		isLeftMobile,
		isRightMobile,
		openMobileSide,
		toggleLeftMobile,
		setLeftMobileOpen,
		toggleRightMobile,
		setRightMobileOpen,
		leftState,
		filledSide,
		toggleLeftFill,
		setLeftFill,
		leftVariant,
		leftWidth,
		leftDragWidth,
		leftCollapsible,
		onLeftWidthChange,
		isLeftDragging,
		rightState,
		toggleRightFill,
		setRightFill,
		rightVariant,
		rightWidth,
		rightDragWidth,
		rightCollapsible,
		onRightWidthChange,
		isRightDragging
	]);
	return /* @__PURE__ */ jsx(SidebarContext, {
		value,
		children
	});
};
// src/components/Sidebar/SidebarFillTrigger.tsx
const SidebarFillTrigger = ({ ref, side = "left", onClick, ...rest }) => {
	const { toggleFill, isFill } = useSidebar(side);
	const handleOnClick = (event) => {
		toggleFill();
		onClick?.(event);
	};
	return /* @__PURE__ */ jsx(Slot, {
		...rest,
		ref,
		onClick: handleOnClick,
		"aria-selected": isFill
	});
};
// src/components/Sidebar/SidebarFooter.tsx
const SidebarFooter = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	"data-slot": "sidebar-footer",
	className: cn("tw:flex", "tw:flex-col", "tw:gap-2", "tw:p-2", className),
	...props
});
// src/components/Sidebar/SidebarGroup/SidebarGroup.tsx
const SidebarGroup = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:group/sidebar-group", "tw:relative", "tw:w-full", "tw:flex", "tw:flex-col", "tw:min-w-0", "tw:rounded-lg", className),
	...props
});
// src/components/Sidebar/SidebarGroup/SidebarGroupContent.tsx
const SidebarGroupContent = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:flex", "tw:flex-col", "tw:gap-2", "tw:w-full", className),
	...props
});
// src/components/Sidebar/SidebarGroup/SidebarGroupAction.tsx
const SidebarGroupAction = ({ ref, className, type = "button", children, ...props }) => /* @__PURE__ */ jsx("button", {
	...props,
	type,
	ref,
	className: cn("tw:group-data-[state=collapsed]/sidebar:hidden", "tw:absolute", "tw:right-1.5", "tw:top-2", "tw:flex", "tw:items-center", "tw:justify-center", "tw:size-5", "tw:text-sidebar-foreground", "tw:rounded-full", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-3", "tw:[&_svg]:text-muted-foreground", "tw:hover:[&_svg]:text-secondary-600", "tw:hover:bg-muted", "tw:focus-visible:ring-4", "tw:focus-visible:ring-ring/30", "tw:focus-visible:outline", "tw:focus-visible:outline-1", "tw:focus-visible:outline-ring", className),
	children
});
// src/components/Sidebar/SidebarGroup/SidebarGroupLabel.tsx
const SidebarGroupLabel = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsxs("div", {
	ref,
	className: cn("tw:relative", "tw:flex", "tw:flex-col", "tw:shrink-0", "tw:h-8", "tw:px-2", "tw:-mb-1", "tw:rounded-lg", "tw:uppercase", "tw:font-sans", "tw:text-xs", "tw:tracking-[1px]", className),
	...props,
	children: [/* @__PURE__ */ jsx(Separator, {
		orientation: "horizontal",
		size: "3/4",
		className: cn("tw:opacity-0", "tw:group-data-[state=collapsed]/sidebar:opacity-100", "tw:absolute", "tw:w-full", "tw:left-0", "tw:top-1/2 tw:-translate-y-1/2", "tw:transition-opacity", "tw:duration-(--sidebar-motion-duration)", "tw:ease-(--sidebar-motion-easing)", "tw:motion-reduce:transition-none")
	}), /* @__PURE__ */ jsx("span", {
		className: cn("tw:flex", "tw:group-data-[state=collapsed]/sidebar:opacity-0", "tw:items-center", "tw:flex-1", "tw:text-muted-foreground", "tw:whitespace-nowrap", "tw:transition-opacity", "tw:duration-(--sidebar-motion-duration)", "tw:ease-(--sidebar-motion-easing)", "tw:motion-reduce:transition-none"),
		children
	})]
});
// src/components/Sidebar/SidebarInstanceContext.ts
const SidebarInstanceContext = createContext(null);
const useSidebarInstance = () => {
	const context = use(SidebarInstanceContext);
	if (context === null) throw new Error("useSidebarInstance must be used within a SidebarInstanceContext.");
	return context;
};
// src/components/Sidebar/SidebarHeader.tsx
const SidebarHeader = ({ className, children, ...props }) => {
	const variant = use(SidebarInstanceContext)?.state === "collapsed" ? "mark" : "wordmark";
	const fallback = /* @__PURE__ */ jsx(BrandLogo, {
		variant,
		className: "tw:h-7"
	});
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sidebar-header",
		className: cn("tw:peer/sidebar-header", "tw:flex", "tw:flex-col", "tw:gap-2", "tw:p-2", className),
		...props,
		children: children ?? fallback
	});
};
// src/components/Sidebar/SidebarMenu/SidebarMenu.tsx
const SidebarMenu = ({ className, ...props }) => /* @__PURE__ */ jsx("ul", {
	"data-sidebar": "menu",
	className: cn("tw:flex", "tw:w-full", "tw:min-w-0", "tw:flex-col", "tw:gap-1", "tw:transition-[margin,opacity]", "tw:duration-(--sidebar-motion-duration)", "tw:ease-(--sidebar-motion-easing)", "tw:motion-reduce:transition-none", "tw:pl-0", className),
	...props
});
// src/components/Sidebar/SidebarMenu/SidebarMenuIcon.tsx
const SidebarMenuIcon = ({ ref, className, children, ...rest }) => /* @__PURE__ */ jsx("span", {
	...rest,
	ref,
	className: cn("tw:inline-flex", "tw:items-center", "tw:justify-center", "tw:aspect-square", "tw:shrink-0", "tw:p-0.5", "tw:text-sidebar-primary", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", className),
	children
});
// src/components/Sidebar/SidebarMenu/useDestinationDismiss.ts
/**
* A menu item's click handler: the caller's own, then (on a phone) dismissing the sheet.
*
* - Not a destination, so no dismissal:
*   - A popup opener (`aria-haspopup` / `aria-expanded`) — closing would take the popup too.
*   - `data-sidebar-trigger` — its `toggle` runs after this and would reopen the sheet.
*/
const useDestinationDismiss = (props, onClick) => {
	const { side } = useSidebarInstance();
	const { isMobile, setMobileOpen } = useSidebar(side);
	const isDestination = props["aria-haspopup"] === void 0 && props["aria-expanded"] === void 0 && props["data-sidebar-trigger"] === void 0;
	return (event) => {
		onClick?.(event);
		if (isMobile && isDestination) setMobileOpen(false);
	};
};
// src/components/Sidebar/SidebarMenu/SidebarMenuButton.tsx
/** A collapsed rail is icon-only, so its labels appear almost at once instead of after the default 300ms. */
const COLLAPSED_TOOLTIP_DELAY_MS = 50;
const SidebarMenuButton = ({ ref, "aria-selected": selected, className, children, asChild = false, icon, tooltip, onClick, ...props }) => {
	const Component = asChild ? Root : "button";
	const { state, mode, side } = useSidebarInstance();
	const handleClick = useDestinationDismiss(props, onClick);
	const button = /* @__PURE__ */ jsxs(Component, {
		...props,
		onClick: handleClick,
		ref,
		"data-slot": "sidebar-menu-button",
		...asChild ? {} : { type: "button" },
		...selected === void 0 ? {} : { "aria-selected": selected },
		className: cn("tw:peer/sidebar-menu-button", "tw:group/sidebar-menu-button", "tw:group-data-[state=expanded]/sidebar:group-has-[[data-slot=sidebar-menu-action][data-hover-only=false]]/sidebar-menu-item:pr-8", "tw:group-data-[state=expanded]/sidebar:[&:has(+[data-slot=\"sidebar-menu-action\"])]:group-hover/sidebar-menu-item:pr-8", "tw:group-data-[state=expanded]/sidebar:[&:has(+[data-slot=\"sidebar-menu-action\"])]:group-focus-within/sidebar-menu-item:pr-8", "tw:group-data-[state=expanded]/sidebar:[&:has(+[data-slot=\"sidebar-menu-action\"][data-hover-only=true][data-state=open])]:pr-8", "tw:flex", "tw:w-full", "tw:gap-2", "tw:items-center", "tw:z-10", "tw:font-normal", "tw:text-sidebar-foreground", "tw:p-2", "tw:whitespace-nowrap", "tw:overflow-hidden", "tw:rounded-lg", "tw:text-start", "tw:no-underline", "tw:select-none", "tw:hover:not-disabled:bg-sidebar-hover", "tw:hover:text-sidebar-hover-foreground", "tw:focus:text-sidebar-hover-foreground", "tw:data-[slot=dropdown-menu-trigger]:data-[state=open]:bg-sidebar-hover", "tw:disabled:opacity-disabled", "tw:disabled:cursor-not-allowed", "tw:aria-selected:font-medium", "tw:aria-selected:bg-sidebar-selected", "tw:aria-selected:hover:not-disabled:bg-sidebar-selected", "tw:aria-selected:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]", "tw:aria-selected:hover:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:aria-selected:focus:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:aria-selected:cursor-default", "tw:aria-[current=page]:bg-sidebar-selected", "tw:aria-[current=page]:hover:not-disabled:bg-sidebar-selected", "tw:aria-[current=page]:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]", "tw:aria-[current=page]:hover:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:aria-[current=page]:font-medium", "tw:aria-[current=page]:focus:text-sidebar-selected-foreground", "tw:focus-visible:outline-hidden", "tw:focus-visible:z-10", "tw:focus-visible:ring-2", "tw:focus-visible:ring-sidebar-ring/50", "tw:focus-visible:text-sidebar-foreground", className),
		tabIndex: 0,
		children: [icon && /* @__PURE__ */ jsx(SidebarMenuIcon, { children: icon }), /* @__PURE__ */ jsx(Slottable, { children })]
	});
	if (state === "collapsed" && mode !== "expand-on-hover" && tooltip) return /* @__PURE__ */ jsx(Tooltip, {
		tooltip,
		placement: side === "right" ? "left" : "right",
		delayDuration: COLLAPSED_TOOLTIP_DELAY_MS,
		triggerAsChild: true,
		children: button
	});
	return button;
};
// src/components/Sidebar/SidebarMenu/SidebarMenuItem.tsx
const SidebarMenuItem = ({ className, ...props }) => /* @__PURE__ */ jsx("li", {
	className: cn("tw:group/sidebar-menu-item", "tw:relative", className),
	...props
});
// src/components/Sidebar/SidebarMenu/SidebarMenuAction.tsx
const SidebarMenuAction = ({ ref, className, asChild = false, showOnHover = false, children, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "button", {
		...props,
		"data-slot": "sidebar-menu-action",
		"data-hover-only": showOnHover,
		ref,
		className: cn("tw:absolute", "tw:top-1/2", "tw:-translate-y-1/2", "tw:right-3", "tw:z-10", "tw:p-0.5", "tw:flex", "tw:items-center", "tw:justify-center", "tw:aspect-square", "tw:data-[hover-only=true]:opacity-0", "tw:group-data-[state=collapsed]/sidebar:hidden", "tw:group-data-[state=expanded]/sidebar:group-hover/sidebar-menu-item:opacity-100", "tw:group-data-[state=expanded]/sidebar:group-focus-within/sidebar-menu-item:opacity-100", "tw:group-data-[state=expanded]/sidebar:data-[hover-only=true]:data-[state=open]:opacity-100", "tw:size-4", "tw:rounded", "tw:[&_svg]:shrink-0", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-3.5", "tw:[&_svg]:text-sidebar-accent-foreground", "tw:focus-visible:outline-hidden", "tw:focus-visible:z-10", "tw:focus-visible:ring-2", "tw:focus-visible:ring-sidebar-ring/50", "tw:focus-visible:text-sidebar-foreground", className),
		children
	});
};
// src/components/Sidebar/SidebarMenu/SidebarSearchInput.tsx
const wrapperClassnames = cn("tw:group/search-input", "tw:relative", "tw:flex", "tw:items-center", "tw:min-h-9", "tw:rounded-lg", "tw:border", "tw:transition-all", "tw:group-data-[state=collapsed]/sidebar:cursor-pointer", "tw:group-data-[state=collapsed]/sidebar:border-transparent", "tw:group-data-[state=collapsed]/sidebar:hover:bg-sidebar-hover", "tw:group-data-[state=expanded]/sidebar:cursor-text", "tw:group-data-[state=expanded]/sidebar:px-2", "tw:group-data-[state=expanded]/sidebar:border-sidebar-border", "tw:focus-within:ring-2", "tw:focus-within:ring-sidebar-ring/50", "tw:focus-within:text-sidebar-foreground");
const SidebarSearchInput = ({ ref: forwardedRef, className, ...inputProps }) => {
	const { side } = useSidebarInstance();
	const { setMode } = useSidebar(side);
	const inputRef = useRef(null);
	const handleFocus = () => {
		setMode("expanded");
	};
	return /* @__PURE__ */ jsxs("div", {
		className: cn(wrapperClassnames, className),
		children: [/* @__PURE__ */ jsx(Search, { className: cn("tw:pointer-events-none", "tw:absolute", "tw:left-2", "tw:size-4", "tw:text-sidebar-primary", "tw:group-data-[state=expanded]/sidebar:text-muted-foreground") }), /* @__PURE__ */ jsx("input", {
			ref: mergeRefs(forwardedRef ?? null, inputRef),
			...inputProps,
			onFocus: (e) => {
				handleFocus();
				inputProps.onFocus?.(e);
			},
			className: cn("tw:w-full", "tw:bg-transparent", "tw:text-sm", "tw:font-sans", "tw:outline-hidden", "tw:placeholder:text-muted-foreground", "tw:group-data-[state=collapsed]/sidebar:opacity-0", "tw:group-data-[state=collapsed]/sidebar:cursor-pointer", "tw:group-data-[state=expanded]/sidebar:pl-6")
		})]
	});
};
// src/components/Sidebar/SidebarMenu/SidebarMenuCard.tsx
const Title = ({ children }) => /* @__PURE__ */ jsx("div", {
	className: "tw:flex tw:select-none tw:gap-2 tw:text-sm tw:font-semibold",
	children
});
const Content = ({ children, className }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:whitespace-pre-line", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:invisible", className),
	children
});
const SidebarMenuCard = ({ children, className, icon, title, ...rest }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { side = "left" } = useSidebarInstance();
	const { state, isFill } = useSidebar(side);
	const isExpanded = state === "expanded" || isFill;
	const handleOnOpenChange = useCallback((open) => {
		setIsOpen(isExpanded || document.hidden ? false : open);
	}, [isExpanded]);
	return /* @__PURE__ */ jsxs(HoverCard, {
		open: isOpen,
		onOpenChange: handleOnOpenChange,
		children: [/* @__PURE__ */ jsx(HoverCard.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsx("div", {
				...rest,
				"data-slot": "sidebar-menu-card",
				tabIndex: isExpanded ? -1 : 0,
				className: cn("tw:group/sidebar-menu-card", "tw:peer/sidebar-menu-card", "tw:overflow-hidden", "tw:border", "tw:border-sidebar-border", "tw:rounded-2xl", "tw:grid", "tw:grid-rows-[1fr]", "tw:flex-1", "tw:p-4", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:p-2", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:border-transparent", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:grid-rows-[18px]", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:rounded-md", "tw:outline-hidden", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:hover:bg-sidebar-hover", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:focus-visible:ring-2", "tw:group-data-[fill=false]/sidebar:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:focus-visible:ring-sidebar-ring/50", "tw:transition-[padding,border]", "tw:duration-(--sidebar-motion-duration)", "tw:ease-(--sidebar-motion-easing)", "tw:motion-reduce:transition-none"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:flex-col tw:gap-2",
					children: [/* @__PURE__ */ jsxs(Title, { children: [/* @__PURE__ */ jsx(SidebarMenuIcon, {
						className: "tw:p-px",
						children: icon
					}), title] }), /* @__PURE__ */ jsx(Content, {
						className,
						children
					})]
				})
			})
		}), /* @__PURE__ */ jsxs(HoverCard.Content, {
			sideOffset: 10,
			align: "center",
			side: side === "left" ? "right" : "left",
			className: "tw:flex tw:flex-col tw:gap-2",
			children: [/* @__PURE__ */ jsx(Title, { children: title }), /* @__PURE__ */ jsx(Content, {
				className,
				children
			})]
		})]
	});
};
// src/components/Sidebar/SidebarMenu/SidebarMenuBadge.tsx
const SidebarMenuBadge = ({ ref, className, ...props }) => {
	return /* @__PURE__ */ jsx("div", {
		ref,
		"data-slot": "sidebar-menu-badge",
		"data-sidebar": "menu-badge",
		className: cn("tw:flex", "tw:items-center", "tw:justify-center", "tw:z-20", "tw:absolute", "tw:peer-[]/sidebar-menu-button:right-2", "tw:peer-[]/sidebar-menu-card:right-4", "tw:peer-[]/sidebar-menu-card:top-4", "tw:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:right-0", "tw:group-data-[state=collapsed]/sidebar:group-data-[collapsible=icon]/sidebar:-top-1", "tw:text-xs", "tw:text-sidebar-primary-foreground", "tw:bg-sidebar-primary", "tw:px-1", "tw:min-w-5", "tw:h-5", "tw:rounded-full", "tw:pointer-events-none", "tw:font-medium", "tw:tabular-nums", "tw:select-none", "tw:transition-[top,right]", "tw:delay-50", "tw:duration-(--sidebar-motion-duration)", "tw:ease-(--sidebar-motion-easing)", "tw:motion-reduce:transition-none", className),
		...props
	});
};
// src/components/Sidebar/SidebarMenu/SidebarMenuSub.tsx
const SidebarMenuSub = ({ children, icon, isActive = false, label, defaultOpen = false, open: openProp, onOpenChange }) => {
	const { state, side, mode } = useSidebarInstance();
	const [open, setOpen] = useState(null);
	const [innerCollapsibleOpen, setInnerCollapsibleOpen] = useState(defaultOpen);
	const isCollapsibleOpen = (() => {
		if (state === "collapsed") return false;
		return openProp ?? innerCollapsibleOpen;
	})();
	const isDropdownMenu = mode === "expand-on-hover" || mode === "collapsed";
	const handleCollapsibleOpenChange = (open) => {
		if (openProp === void 0) setInnerCollapsibleOpen(open);
		onOpenChange?.(open);
	};
	const handleDropdownOpenChange = (open) => {
		setOpen(open ? "open-dropdown-menu" : null);
	};
	const handleTooltipOpenChange = (open) => {
		setOpen((currentOpen) => {
			if (currentOpen === "open-dropdown-menu") return "open-dropdown-menu";
			if (state === "expanded") return null;
			return open ? "open-tooltip" : null;
		});
	};
	if (isDropdownMenu) return /* @__PURE__ */ jsxs(DropdownMenu, {
		open: open === "open-dropdown-menu",
		onOpenChange: handleDropdownOpenChange,
		children: [/* @__PURE__ */ jsxs(Tooltip.Root, {
			open: open === "open-tooltip",
			onOpenChange: handleTooltipOpenChange,
			children: [/* @__PURE__ */ jsx(Tooltip.Trigger, {
				asChild: true,
				children: /* @__PURE__ */ jsx(DropdownMenu.Trigger, {
					asChild: true,
					children: /* @__PURE__ */ jsx(SidebarMenuButton, {
						icon,
						"data-active": isActive,
						className: cn("tw:data-[active=true]:bg-sidebar-selected", "tw:data-[active=true]:hover:not-disabled:bg-sidebar-selected", "tw:data-[active=true]:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]", "tw:data-[active=true]:hover:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:data-[active=true]:font-medium"),
						children: label
					})
				})
			}), /* @__PURE__ */ jsx(Tooltip.Content, {
				variant: "action",
				placement: side,
				children: label
			})]
		}), /* @__PURE__ */ jsxs(DropdownMenu.Content, {
			side,
			align: "start",
			children: [/* @__PURE__ */ jsx(DropdownMenu.Label, { children: label }), children]
		})]
	});
	return /* @__PURE__ */ jsxs(Collapsible, {
		open: isCollapsibleOpen,
		onOpenChange: handleCollapsibleOpenChange,
		children: [/* @__PURE__ */ jsx(Collapsible.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsxs(SidebarMenuButton, {
				icon,
				"data-active": isActive && !isCollapsibleOpen,
				className: cn("tw:group", "tw:data-[active=true]:bg-sidebar-selected", "tw:data-[active=true]:hover:not-disabled:bg-sidebar-selected", "tw:data-[active=true]:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]", "tw:data-[active=true]:hover:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:data-[active=true]:font-medium"),
				children: [label, /* @__PURE__ */ jsx(ChevronDown, { className: "tw:ml-auto tw:size-5 tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-180" })]
			})
		}), /* @__PURE__ */ jsx(Collapsible.Content, { children: /* @__PURE__ */ jsx("ul", {
			"data-sidebar": "menu-sub",
			className: cn("tw:ml-3.5", "tw:flex", "tw:min-w-0", "tw:translate-x-px", "tw:flex-col", "tw:gap-1", "tw:border-l", "tw:border-sidebar-border", "tw:py-0.5", "tw:pl-2.5", "tw:pr-0", "tw:list-none"),
			children
		}) })]
	});
};
// src/components/Sidebar/SidebarMenu/SidebarMenuSubItem.tsx
const SidebarMenuSubItem = ({ ref, className, asChild = false, children, disabled, onClick, "aria-current": ariaCurrent, ...props }) => {
	const { mode } = useSidebarInstance();
	const handleClick = useDestinationDismiss(props, onClick);
	if (mode === "collapsed" || mode === "expand-on-hover") return /* @__PURE__ */ jsx(DropdownMenu.Item, {
		"aria-current": ariaCurrent,
		asChild,
		disabled,
		className: cn("tw:focus:!bg-sidebar-hover", "tw:aria-[current=page]:bg-sidebar-selected", "tw:aria-[current=page]:focus:!bg-sidebar-selected", "tw:aria-[current=page]:focus:text-sidebar-selected-foreground", className),
		children
	});
	return /* @__PURE__ */ jsx("li", {
		className: "tw:group/sidebar-menu-sub-item tw:relative",
		children: /* @__PURE__ */ jsx(asChild ? Root : "button", {
			...props,
			onClick: handleClick,
			ref,
			"aria-current": ariaCurrent,
			"data-slot": "sidebar-menu-sub-button",
			type: "button",
			disabled,
			className: cn("tw:flex", "tw:items-center", "tw:gap-2", "tw:h-7", "tw:w-full", "tw:min-w-0", "tw:px-2", "tw:text-sidebar-foreground", "tw:text-sm", "tw:font-normal", "tw:text-start", "tw:whitespace-nowrap", "tw:overflow-hidden", "tw:rounded-md", "tw:no-underline", "tw:select-none", "tw:outline-hidden", "tw:hover:not-disabled:bg-sidebar-hover", "tw:hover:text-sidebar-hover-foreground", "tw:focus:text-sidebar-hover-foreground", "tw:aria-selected:font-medium", "tw:aria-selected:bg-sidebar-selected", "tw:aria-selected:hover:not-disabled:bg-sidebar-selected", "tw:aria-selected:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]", "tw:aria-selected:hover:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:aria-selected:focus:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:aria-[current=page]:bg-sidebar-selected", "tw:aria-[current=page]:hover:not-disabled:bg-sidebar-selected", "tw:aria-[current=page]:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]", "tw:aria-[current=page]:hover:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:aria-[current=page]:focus:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-accent-foreground)))]", "tw:aria-[current=page]:font-medium", "tw:disabled:opacity-disabled", "tw:disabled:cursor-not-allowed", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-sidebar-ring/50", className),
			children: /* @__PURE__ */ jsx(Slottable, { children })
		})
	});
};
// src/components/Sidebar/useResizeSidebar.ts
const DEFAULT_MIN_WIDTH = 384;
const DEFAULT_MAX_WIDTH = 800;
const useResizeSidebar = ({ side, minWidth = DEFAULT_MIN_WIDTH, maxWidth = DEFAULT_MAX_WIDTH }) => {
	const { onWidthChange, baseWidth, isFill, state, isDragging, setIsDragging, setDragWidth } = useSidebar(side);
	const isResizable = state === "expanded" && !isFill && onWidthChange !== void 0;
	const startXRef = useRef(0);
	const startWidthRef = useRef(0);
	const cleanupRef = useRef(null);
	useEffect(() => {
		return () => {
			cleanupRef.current?.();
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
	}, []);
	const clampToRange = useCallback((width) => {
		const effectiveMax = Math.max(minWidth, Math.min(maxWidth, window.innerWidth * .5));
		return Math.round(Math.min(effectiveMax, Math.max(minWidth, width)));
	}, [minWidth, maxWidth]);
	return {
		handleProps: {
			onMouseDown: useCallback((e) => {
				if (!isResizable || isDragging) return;
				cleanupRef.current?.();
				setIsDragging(true);
				startWidthRef.current = baseWidth ?? SidebarWidthInPx.MIN_DEFAULT;
				startXRef.current = e.clientX;
				const handleMouseMove = (moveEvent) => {
					const delta = side === "right" ? startXRef.current - moveEvent.clientX : moveEvent.clientX - startXRef.current;
					setDragWidth(clampToRange(startWidthRef.current + delta));
				};
				const handleMouseUp = (upEvent) => {
					const delta = side === "right" ? startXRef.current - upEvent.clientX : upEvent.clientX - startXRef.current;
					onWidthChange?.(clampToRange(startWidthRef.current + delta));
					setIsDragging(false);
					document.removeEventListener("mousemove", handleMouseMove);
					document.removeEventListener("mouseup", handleMouseUp);
					document.body.style.cursor = "";
					document.body.style.userSelect = "";
					cleanupRef.current = null;
				};
				document.body.style.cursor = "col-resize";
				document.body.style.userSelect = "none";
				document.addEventListener("mousemove", handleMouseMove);
				document.addEventListener("mouseup", handleMouseUp);
				cleanupRef.current = () => {
					document.removeEventListener("mousemove", handleMouseMove);
					document.removeEventListener("mouseup", handleMouseUp);
					setIsDragging(false);
					setDragWidth(null);
				};
			}, [
				isResizable,
				isDragging,
				side,
				onWidthChange,
				baseWidth,
				setIsDragging,
				setDragWidth,
				clampToRange
			]),
			onDoubleClick: useCallback(() => {
				onWidthChange?.(minWidth);
			}, [onWidthChange, minWidth])
		},
		isDragging,
		isResizable
	};
};
// src/components/Sidebar/SidebarResizeHandle.tsx
const SidebarResizeHandle = ({ side, minWidth, maxWidth }) => {
	const { handleProps, isResizable } = useResizeSidebar({
		side,
		minWidth,
		maxWidth
	});
	if (!isResizable) return null;
	return /* @__PURE__ */ jsx("div", {
		...handleProps,
		"aria-hidden": true,
		className: cn("tw:absolute tw:inset-y-0 tw:z-10 tw:w-3", side === "right" ? "tw:left-0" : "tw:right-0", "tw:cursor-col-resize")
	});
};
// src/components/Sidebar/SidebarSeparator.tsx
const SidebarSeparator = ({ className, ...props }) => /* @__PURE__ */ jsx(Separator, {
	...props,
	orientation: "horizontal",
	size: "full",
	className: cn("tw:before:bg-sidebar-border tw:p-2", className)
});
// src/components/Sidebar/SidebarTrigger.tsx
const SidebarTrigger = ({ side = "left", onClick, hideWhenCollapsed = false, className, ...rest }) => {
	const { toggle, state, isMobile, isMobileOpen } = useSidebar(side);
	const handleOnClick = (event) => {
		toggle();
		onClick?.(event);
	};
	return /* @__PURE__ */ jsx(Slot, {
		...rest,
		"aria-expanded": isMobile ? isMobileOpen : state === "expanded",
		...isMobile ? { "aria-haspopup": "dialog" } : null,
		"data-sidebar-trigger": "",
		onClick: handleOnClick,
		className: cn({ "tw:group-data-[state=collapsed]/sidebar:hidden": hideWhenCollapsed }, className)
	});
};
// src/components/Sidebar/Sidebar.tsx
const Sidebar = ({ children, className, id, side = "left", style, container = false, keepMounted, mountAfterEnter, resizable = false, mobileTitle, ...rest }) => {
	const oppositeSide = side === "left" ? "right" : "left";
	const { setSidebarElement, state, mode, isFill, widths, collapsible, variant, isDragging, isMobile, isMobileOpen, setMobileOpen } = useSidebar(side);
	const themeScopeContainer = useThemeScopeContainer();
	const { isMounted, completeUnmount } = useTransitionPresence({
		mounted: collapsible !== "off-canvas" || mode !== "collapsed",
		keepMounted,
		mountAfterEnter
	});
	const { widths: oppositeWidths } = useSidebar(oppositeSide);
	const sidebarInstanceContextValue = useMemo(() => ({
		state,
		mode,
		side
	}), [
		state,
		mode,
		side
	]);
	const focusOriginRef = useRef(null);
	useEffect(() => {
		if (!isMobile || isMobileOpen) return;
		const remember = (event) => {
			if (event.target instanceof HTMLElement) focusOriginRef.current = event.target;
		};
		document.addEventListener("focusin", remember);
		return () => document.removeEventListener("focusin", remember);
	}, [isMobile, isMobileOpen]);
	const wasSheetUpRef = useRef(false);
	useEffect(() => {
		const wasSheetUp = wasSheetUpRef.current;
		wasSheetUpRef.current = isMobile && isMobileOpen;
		if (!wasSheetUp || isMobile) return;
		const origin = focusOriginRef.current;
		if (origin?.isConnected) origin.focus();
	}, [isMobile, isMobileOpen]);
	const handleTransitionEnd = (event) => {
		if (event.target !== event.currentTarget) return;
		completeUnmount();
	};
	const styles = {
		...style,
		"--sidebar-floating-padding": `${SidebarWidthInPx.FLOATING_VARIANT_PADDING}px`,
		"--sidebar-width": `${widths.sidebar}px`,
		"--sidebar-width-gap": `${widths.gap}px`,
		"--opposite-sidebar-width": `${oppositeWidths.fill}px`
	};
	if (variant === "none") return null;
	const railAttributes = {
		"data-state": state,
		"data-side": side,
		"data-mode": mode,
		"data-collapsible": collapsible,
		"data-variant": variant,
		"data-fill": isFill
	};
	if (isMobile) return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsx(SidebarInstanceContext, {
		value: sidebarInstanceContextValue,
		children: /* @__PURE__ */ jsx(Sheet, {
			open: isMobileOpen,
			onOpenChange: setMobileOpen,
			children: /* @__PURE__ */ jsxs(Sheet.Content, {
				side,
				container: themeScopeContainer ?? void 0,
				className: "tw:w-[min(20rem,85vw)] tw:bg-sidebar tw:text-sidebar-foreground tw:p-0",
				onCloseAutoFocus: (event) => {
					const origin = focusOriginRef.current;
					if (!origin?.isConnected) return;
					event.preventDefault();
					origin.focus();
				},
				children: [/* @__PURE__ */ jsx(Sheet.Title, {
					visuallyHidden: true,
					children: mobileTitle ?? (side === "left" ? "Navigation" : "Side panel")
				}), /* @__PURE__ */ jsx("div", {
					...rest,
					...railAttributes,
					id,
					style,
					className: cn("tw:flex tw:min-h-0 tw:flex-1 tw:flex-col", { "tw:group/sidebar tw:peer/sidebar": !container }, className),
					children: /* @__PURE__ */ jsx("div", {
						"data-slot": "sidebar",
						className: cn("tw:bg-sidebar", "tw:relative", "tw:flex", "tw:flex-col", "tw:size-full", "tw:overflow-hidden", "tw:border-sidebar-border", "tw:group-[]/sidebar:pt-2"),
						children
					})
				})]
			})
		})
	}) });
	return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsx(SidebarInstanceContext, {
		value: sidebarInstanceContextValue,
		children: /* @__PURE__ */ jsxs("div", {
			...rest,
			...railAttributes,
			id,
			ref: setSidebarElement,
			style: styles,
			className: cn("tailwindcss-preflight", "tw:flex", "tw:relative", mode === "expand-on-hover" ? "tw:z-[51]" : "tw:z-50", { "tw:group/sidebar tw:peer/sidebar": !container }, "tw:[&>[data-slot=gap]]:w-(--sidebar-width-gap)", "tw:[&[data-side=right]>[data-slot=gap]]:rotate-180", "tw:[&>[data-slot=sidebar-wrapper]]:w-(--sidebar-width)", "tw:[&[data-side=left]>[data-slot=sidebar-wrapper]]:left-0", "tw:[&[data-side=right]>[data-slot=sidebar-wrapper]]:right-0", "tw:[&[data-state=collapsed][data-collapsible=off-canvas][data-side=left]>[data-slot=sidebar-wrapper]]:-translate-x-full", "tw:[&[data-state=collapsed][data-collapsible=off-canvas][data-side=right]>[data-slot=sidebar-wrapper]]:translate-x-full", "tw:[&[data-variant=sided-floating]>[data-slot=sidebar-wrapper]]:pt-(--sidebar-floating-padding)", "tw:[&[data-variant=sided-floating][data-side=left]>[data-slot=sidebar-wrapper]]:pr-(--sidebar-floating-padding)", "tw:[&[data-variant=sided-floating][data-side=right]>[data-slot=sidebar-wrapper]]:pl-(--sidebar-floating-padding)", "tw:[&[data-variant=floating]>[data-slot=sidebar-wrapper]]:p-(--sidebar-floating-padding)", "tw:[&[data-fill=true]>[data-slot=sidebar-wrapper]]:!w-[calc(100svw-var(--opposite-sidebar-width))]", "tw:[&[data-fill=true][data-variant=floating]>[data-slot=sidebar-wrapper]]:!p-(--sidebar-floating-padding)", "tw:[&[data-fill=true][data-variant=sided-floating][data-side=left]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:!rounded-tr-xl", "tw:[&[data-fill=true][data-variant=sided-floating][data-side=right]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:!rounded-tl-xl", "tw:[&[data-variant=inset][data-side=left]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:border-r", "tw:[&[data-variant=inset][data-side=right]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:border-l", "tw:[&[data-variant=floating]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:shadow", "tw:[&[data-variant=floating]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:rounded-xl", "tw:[&[data-variant=sided-floating]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:shadow", "tw:[&[data-variant=sided-floating][data-side=left]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:rounded-tr-xl", "tw:[&[data-variant=sided-floating][data-side=right]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:rounded-tl-xl", "tw:[&[data-variant=sided-floating][data-side=left]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:border-r", "tw:[&[data-variant=sided-floating][data-side=right]>[data-slot=sidebar-wrapper]>[data-slot=sidebar]]:border-l", isDragging ? "tw:transition-none" : "tw:transition-[width,translate,transform] tw:duration-(--sidebar-motion-duration) tw:ease-(--sidebar-motion-easing) tw:motion-reduce:transition-none"),
			children: [/* @__PURE__ */ jsx("div", {
				"data-slot": "gap",
				className: cn("tw:bg-transparent", isDragging ? "" : "tw:transition-[width] tw:duration-(--sidebar-motion-duration) tw:ease-(--sidebar-motion-easing) tw:motion-reduce:transition-none")
			}), /* @__PURE__ */ jsxs("div", {
				"data-slot": "sidebar-wrapper",
				onTransitionEnd: handleTransitionEnd,
				className: cn("tw:fixed", "tw:inset-y-0", "tw:border-border", isDragging ? "tw:transition-none" : "tw:transition-[width,translate,transform] tw:duration-(--sidebar-motion-duration) tw:ease-(--sidebar-motion-easing) tw:motion-reduce:transition-none", "tw:transform-gpu", className),
				style,
				children: [resizable && /* @__PURE__ */ jsx(SidebarResizeHandle, { side }), /* @__PURE__ */ jsx("div", {
					"data-slot": "sidebar",
					className: cn("tw:bg-sidebar", "tw:overflow-hidden", "tw:relative", "tw:flex", "tw:flex-col", "tw:size-full", "tw:border-sidebar-border", "tw:group-[]/sidebar:pt-2"),
					children: isMounted ? children : null
				})]
			})]
		})
	}) });
};
Sidebar.Header = SidebarHeader;
Sidebar.Content = SidebarContent;
Sidebar.Footer = SidebarFooter;
Sidebar.Trigger = SidebarTrigger;
Sidebar.FillTrigger = SidebarFillTrigger;
Sidebar.Separator = SidebarSeparator;
Sidebar.MenuIcon = SidebarMenuIcon;
Sidebar.Group = SidebarGroup;
Sidebar.GroupLabel = SidebarGroupLabel;
Sidebar.GroupContent = SidebarGroupContent;
Sidebar.GroupAction = SidebarGroupAction;
Sidebar.Menu = SidebarMenu;
Sidebar.MenuItem = SidebarMenuItem;
Sidebar.MenuButton = SidebarMenuButton;
Sidebar.MenuAction = SidebarMenuAction;
Sidebar.SearchInput = SidebarSearchInput;
Sidebar.MenuCard = SidebarMenuCard;
Sidebar.MenuBadge = SidebarMenuBadge;
Sidebar.SubMenu = SidebarMenuSub;
Sidebar.SubItem = SidebarMenuSubItem;
export { Sidebar, SidebarProvider, useOptionalSidebar, useSidebar };

//# sourceMappingURL=Sidebar-C2V2d7tG.js.map